import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useMyTasks } from "@/hooks/use-my-tasks";
import { usePMInbox } from "@/hooks/use-pm-inbox";
import { MyTasksList } from "@/components/time-registration/MyTasksList";
import { PMInboxSection } from "@/components/time-registration/pm-inbox/PMInboxSection";
import WeeklyTasksList from "@/components/time-registration/WeeklyTasksList";
import InternalProjectsSection from "@/components/time-registration/InternalProjectsSection";
import ReviewedTasksSection from "@/components/time-registration/ReviewedTasksSection";
import MonthlyEntriesCard from "@/components/time-registration/MonthlyEntriesCard";
import CloseMonthBanner from "@/components/time-registration/CloseMonthBanner";
import NewRegistrationPopup from "@/components/time-registration/NewRegistrationPopup";
import TimebankCard from "@/components/time-registration/TimebankCard";
import { cn } from "@/lib/utils";
import { startOfWeek, addDays } from "date-fns";
import { SrIcon } from "@/components/ui/SrIcon";
import { ReportAbsenceDialog } from "@/components/shared/ReportAbsenceDialog";

type Tab = "tasks" | "pm" | "week";

export default function TimeRegistrationPage() {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const year = new Date().getFullYear();
  const [showNewPopup, setShowNewPopup] = useState(
    () => searchParams.get("ny") !== null,
  );
  const newBtnRef = useRef<HTMLButtonElement>(null);
  // VIB-1221: løpende fraværsregistrering fra Mine oppgaver (ikke bare ved
  // månedsslutt).
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);

  const { data: myTasksData } = useMyTasks(userId);
  const { data: pmInbox } = usePMInbox(userId);
  const internalProjects = myTasksData?.internalProjects ?? [];
  // Tabben er alltid synlig for innloggede brukere — innboksen viser
  // empty-state per kategori hvis ingen handlinger venter, så PL-er ikke
  // mister tilgang når innboksen tømmes.
  const hasPMProjects = !!userId;

  // Tab state from URL
  const tabParam = searchParams.get("tab");
  const activeTab: Tab =
    tabParam === "pm" && hasPMProjects ? "pm" :
    tabParam === "week" ? "week" :
    "tasks";

  const setActiveTab = (tab: Tab) => {
    setSearchParams(prev => {
      tab === "tasks" ? prev.delete("tab") : prev.set("tab", tab);
      return prev;
    }, { replace: true });
  };

  // Pending count for PM tab badge — alle handlinger på tvers av kategorier
  const pmPendingCount =
    (pmInbox?.approvals.length ?? 0) +
    (pmInbox?.vouchers.length ?? 0) +
    (pmInbox?.invoices.length ?? 0);

  // Shared week state, persisted in URL for back/reload
  const weekParam = searchParams.get("uke");
  const weekStart = weekParam
    ? startOfWeek(new Date(weekParam), { weekStartsOn: 1 })
    : startOfWeek(new Date(), { weekStartsOn: 1 });

  const setWeekStart = (d: Date) => {
    const iso = d.toISOString().split("T")[0];
    const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString().split("T")[0];
    setSearchParams(prev => {
      iso !== currentWeek ? prev.set("uke", iso) : prev.delete("uke");
      return prev;
    }, { replace: true });
  };

  // Keyboard shortcut: N opens "Ny registrering" (unless typing in input)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (showNewPopup) return;
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setShowNewPopup(true);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [showNewPopup]);

  useEffect(() => {
    if (searchParams.get("ny") === null) return;
    setShowNewPopup(true);
    setSearchParams((prev) => {
      prev.delete("ny");
      return prev;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="page-container animate-fade-in">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 min-w-0">
          <SrIcon name="clipboard-list" className="w-6 h-6 text-muted-foreground flex-shrink-0"  />
          <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] text-foreground tracking-wide truncate">
            Mine oppgaver
          </h1>
        </div>
        {activeTab === "tasks" && (
          <div className="flex gap-2 w-full xl:w-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAbsenceDialog(true)}
              className="flex-1 xl:flex-none"
            >
              Meld fravær
            </Button>
            <Button
              ref={newBtnRef}
              size="sm"
              onClick={() => setShowNewPopup(true)}
              className="flex-1 xl:flex-none"
            >
              <SrIcon name="plus" className="w-4 h-4 mr-1"  />
              Ny registrering
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border mb-6">
        <button
          onClick={() => setActiveTab("tasks")}
          className={cn(
            "px-4 py-2.5 text-sm font-medium transition-colors relative",
            activeTab === "tasks"
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Mine oppgaver
          {activeTab === "tasks" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("week")}
          className={cn(
            "px-4 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-1.5",
            activeTab === "week"
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <SrIcon name="calendar-days" className="w-3.5 h-3.5"  />
          Ukevisning
          {activeTab === "week" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
          )}
        </button>
        {hasPMProjects && (
          <button
            onClick={() => setActiveTab("pm")}
            className={cn(
              "px-4 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-2",
              activeTab === "pm"
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <SrIcon name="briefcase" className="w-3.5 h-3.5"  />
            Prosjektledelse
            {pmPendingCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border-[1.5px] border-foreground bg-[#FEF3C7] text-[#78350F]">
                {pmPendingCount}
              </span>
            )}
            {activeTab === "pm" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
            )}
          </button>
        )}
      </div>

      {/* Close month banner */}
      {activeTab === "tasks" && <CloseMonthBanner userId={userId} year={year} />}

      {/* Tab content */}
      {activeTab === "tasks" && (
        <div className="space-y-6">
          <TimebankCard />
          <MyTasksList title="Mine tildelte oppgaver" persistKey="my-tasks" />
          <ReviewedTasksSection />
          {internalProjects.length > 0 && (
            <InternalProjectsSection projects={internalProjects} userId={userId} />
          )}
          <MonthlyEntriesCard userId={userId} weekStart={weekStart} />
        </div>
      )}

      {activeTab === "week" && <WeeklyTasksList />}

      {activeTab === "pm" && (
        <div className="space-y-6">
          <PMInboxSection />
        </div>
      )}

      {showNewPopup && (
        <NewRegistrationPopup
          onClose={() => setShowNewPopup(false)}
          anchorRef={newBtnRef}
        />
      )}

      {userId && (
        <ReportAbsenceDialog
          open={showAbsenceDialog}
          onOpenChange={setShowAbsenceDialog}
          userId={userId}
        />
      )}
    </div>
  );
}
